import { useState } from 'react'
import {
  useUpdateNotificationSubscriptionMutation,
  GetArtboardsForDocumentDocument,
  GetDetailViewArtboardDocument,
  GetShareDocument,
} from 'gqlTypes'
import { useToast } from 'containers/ToastManager'
import {
  readArtboardFromCache,
  readShareListItemFromCache,
  writeArtboardToCache,
  writeShareListItemToCache,
} from 'modules/shares/operations/utils' // Prevent circular dependencies
import {
  readCommentFromCache,
  writeCommentToCache,
} from 'modules/activity/operations'
import {
  DocumentToggleProps,
  ArtboardToggleProps,
  ThreadToggleProps,
} from '../types'

type NextStatus = 'ON' | 'OFF'

const TOAST_TEXT = {
  ON: 'You will now be notified of new comments',
  OFF: 'You will no longer be notified of new comments',
}

/**
 * Hook to update subscription status for Documents, artboards or threads
 */
export const useUpdateNotification = () => {
  const [nextStatus, setNextStatus] = useState<NextStatus | undefined>()
  const { showToast } = useToast()

  /**
   * Generic mutation to update Share, Artboard or Dicussion subscription
   */
  const [updateNotification] = useUpdateNotificationSubscriptionMutation({
    redirectErrors: false,
    onCompleted: ({ updateNotificationSubscription }) => {
      const errors = updateNotificationSubscription?.errors
      const successful = updateNotificationSubscription?.successful

      if (errors?.length) {
        showToast(errors.map(error => error?.message).join('; '), 'negative')
      }

      if (successful && nextStatus) {
        showToast(TOAST_TEXT[nextStatus], 'positive')
      }
    },
    onError: error => {
      showToast(error.message, 'negative')
    },
    optimisticResponse: {
      __typename: 'RootMutationType',
      updateNotificationSubscription: {
        __typename: 'NotificationSubscriptionUpdatedPayload',
        successful: true,
        errors: [],
      },
    },
  })

  /**
   * Mutation to update share subscription
   */
  const updateShareNotification = ({
    id,
    status,
    versionShortId,
  }: DocumentToggleProps & {
    status: NextStatus
  }) => {
    setNextStatus(status)

    updateNotification({
      variables: {
        shareIdentifier: id,
        input: {
          status,
        },
      },
      update: (cache, { data }) => {
        const share = readShareListItemFromCache({ cache, id })

        if (share && data?.updateNotificationSubscription?.successful) {
          writeShareListItemToCache({
            cache,
            share: { ...share, subscriptionStatus: status },
          })
        }
      },
      // Refetch artboards to update automatically the subscription status
      // for every artboard belonging to this share version
      refetchQueries: ({ data }) => {
        if (!data?.updateNotificationSubscription?.successful) return []

        return [
          {
            query: GetArtboardsForDocumentDocument,
            variables: { identifier: id, versionShortId },
          },
        ]
      },
    })
  }

  /**
   * Mutation to update artboard subscription
   */
  const updateArtboardNotification = ({
    id,
    status,
    permanentArtboardShortId,
    documentVersionShortId,
    shareIdentifier,
  }: ArtboardToggleProps & {
    status: NextStatus
  }) => {
    setNextStatus(status)

    updateNotification({
      variables: {
        artboardIdentifier: id,
        input: {
          status,
        },
      },
      update: (cache, { data }) => {
        const artboard = readArtboardFromCache({
          cache,
          id,
          permanentArtboardShortId,
          documentVersionShortId,
        })

        if (artboard && data?.updateNotificationSubscription?.successful) {
          writeArtboardToCache({
            cache,
            artboard: { ...artboard, subscriptionStatus: status },
          })
        }
      },
      // Refetch share to update subscription status
      refetchQueries: ({ data }) => {
        if (!data?.updateNotificationSubscription?.successful) return []

        return [
          {
            query: GetShareDocument,
            variables: { shortId: shareIdentifier },
          },
        ]
      },
    })
  }

  /**
   * Mutation to update thread subscription
   */
  const updateThreadNotification = ({
    id,
    status,
    permanentArtboardShortId,
    documentVersionShortId,
    shareIdentifier,
  }: ThreadToggleProps & {
    status: NextStatus
  }) => {
    setNextStatus(status)

    updateNotification({
      variables: {
        discussionIdentifier: id,
        input: {
          status,
        },
      },
      update: (cache, { data }) => {
        const comment = readCommentFromCache({ cache, id })

        if (comment && data?.updateNotificationSubscription?.successful) {
          writeCommentToCache({
            cache,
            comment: { ...comment, subscriptionStatus: status },
          })
        }
      },
      // Refetch share and artboard to update subscription status
      refetchQueries: ({ data }) => {
        if (!data?.updateNotificationSubscription?.successful) return []

        return [
          {
            query: GetShareDocument,
            variables: { shortId: shareIdentifier },
          },
          {
            query: GetDetailViewArtboardDocument,
            variables: {
              permanentArtboardShortId,
              documentVersionShortId,
              shareIdentifier,
            },
          },
        ]
      },
    })
  }

  return {
    updateShareNotification,
    updateArtboardNotification,
    updateThreadNotification,
  }
}
